import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import StaffSuggestionsPanel from '@/components/roster/StaffSuggestionsPanel';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, UserPlus, UserMinus, Edit2, Save, Calendar, MapPin,
  Clock, AlertTriangle, CheckCircle, XCircle, Briefcase, FileText, Shield, Play, Plus,
} from 'lucide-react';

const SESSION_TYPE_LABELS = {
  group_support: 'Group Support', community_access: 'Community Access', centre_based: 'Centre-Based',
  day_program: 'Day Program', social_recreation: 'Social & Recreation', transport: 'Transport', other: 'Other',
};
const LOCATION_TYPE_LABELS = { centre: 'Centre', house: 'House', community: 'Community', other: 'Other' };
const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700', published: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700', completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};
const STATUS_LABELS = { draft: 'Draft', published: 'Published', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' };
const ROLE_LABELS = { lead: 'Lead', support_worker: 'Support Worker', assistant: 'Assistant', supervisor: 'Supervisor', trainee: 'Trainee' };

export default function GroupSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  // Add participant/staff dialogs
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [addingLink, setAddingLink] = useState(false);

  // Search lists
  const [allParticipants, setAllParticipants] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [participantSearch, setParticipantSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [selectedRole, setSelectedRole] = useState('support_worker');
  const [linkNotes, setLinkNotes] = useState('');

  // Ratio rules
  const [ratioRules, setRatioRules] = useState([]);

  // Day 3: Validation, Publish & Checklist
  const [publishing, setPublishing] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [conflictResult, setConflictResult] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [checklistLoading, setChecklistLoading] = useState(false);

  // Day 4: Activities
  const [activities, setActivities] = useState([]);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityForm, setActivityForm] = useState({ title: '', start_datetime: '', end_datetime: '', notes: '' });
  const [savingActivity, setSavingActivity] = useState(false);

  // Day 5: Service Links
  const [serviceLinks, setServiceLinks] = useState([]);
  const [serviceLinkSummary, setServiceLinkSummary] = useState(null);
  const [generatingLinks, setGeneratingLinks] = useState(false);
  const [reviewingLink, setReviewingLink] = useState(null);
  const [reviewForm, setReviewForm] = useState({ review_status: '', review_notes: '' });

  const loadSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/group-sessions/${sessionId}`);
      setSession(res.data.data);
    } catch (err) {
      toast.error('Failed to load session');
      navigate('/admin/group-sessions');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  const loadChecklist = useCallback(async () => {
    if (!session || session.status !== 'draft') { setChecklist(null); return; }
    setChecklistLoading(true);
    try {
      const res = await api.get(`/group-sessions/${sessionId}/publish-checklist`);
      setChecklist(res.data.data);
    } catch {
      setChecklist(null);
    } finally {
      setChecklistLoading(false);
    }
  }, [sessionId, session]);

  useEffect(() => { loadSession(); }, [loadSession]);
  useEffect(() => { if (session) loadChecklist(); }, [session, loadChecklist]);

  const loadActivities = useCallback(async () => {
    try {
      const res = await api.get(`/group-sessions/${sessionId}/activities`);
      setActivities(res.data.data || []);
    } catch { setActivities([]); }
  }, [sessionId]);

  useEffect(() => { if (session) loadActivities(); }, [session, loadActivities]);

  const loadServiceLinks = useCallback(async () => {
    try {
      const res = await api.get(`/group-sessions/${sessionId}/service-links`);
      setServiceLinks(res.data.data?.links || []);
      setServiceLinkSummary(res.data.data?.summary || null);
    } catch { setServiceLinks([]); }
  }, [sessionId]);

  useEffect(() => { if (session) loadServiceLinks(); }, [session, loadServiceLinks]);

  const handleGenerateLinks = async () => {
    setGeneratingLinks(true);
    try {
      const res = await api.post(`/group-sessions/${sessionId}/generate-service-links`);
      const d = res.data.data;
      toast.success(d.generated ? `Generated ${d.total} service links` : d.message);
      loadServiceLinks();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to generate service links');
    } finally {
      setGeneratingLinks(false);
    }
  };

  const handleReviewLink = async () => {
    if (!reviewingLink) return;
    try {
      await api.patch(`/group-sessions/${sessionId}/service-links/${reviewingLink.id}/review`, reviewForm);
      toast.success('Review updated');
      setReviewingLink(null);
      loadServiceLinks();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to update review');
    }
  };

  useEffect(() => {
    const loadRef = async () => {
      const [pRes, sRes, rRes] = await Promise.allSettled([
        api.get('/participants?page_size=200'),
        api.get('/staff?page_size=200'),
        api.get('/ratio-rules?active_only=true&page_size=100'),
      ]);
      if (pRes.status === 'fulfilled') {
        const pData = pRes.value.data;
        setAllParticipants(pData.data?.items || pData.items || pData.data || []);
      }
      if (sRes.status === 'fulfilled') {
        const sData = sRes.value.data;
        setAllStaff(sData.data?.items || sData.items || sData.data || []);
      }
      if (rRes.status === 'fulfilled') setRatioRules(rRes.value.data.data?.items || []);
    };
    loadRef();
  }, []);

  const isEditable = session && (session.status === 'draft' || session.status === 'published');

  const startEdit = () => {
    setEditForm({
      title: session.title,
      session_type: session.session_type,
      location_type: session.location_type,
      start_datetime: session.start_datetime?.slice(0, 16) || '',
      end_datetime: session.end_datetime?.slice(0, 16) || '',
      required_staff_count: session.required_staff_count,
      ratio_rule_id: session.ratio_rule_id || '',
      billing_mode: session.billing_mode,
      funding_mode: session.funding_mode,
      notes: session.notes || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        required_staff_count: parseInt(editForm.required_staff_count),
        ratio_rule_id: editForm.ratio_rule_id || null,
      };
      await api.put(`/group-sessions/${sessionId}`, payload);
      toast.success('Session updated');
      setEditing(false);
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/group-sessions/${sessionId}`, { status: newStatus });
      toast.success(`Session ${newStatus === 'cancelled' ? 'cancelled' : 'status updated'}`);
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to update status');
    }
  };

  // Day 3: Validated publish flow
  const handlePublish = async (override = false, reason = '') => {
    setPublishing(true);
    try {
      const res = await api.patch(`/group-sessions/${sessionId}/publish`, {
        override,
        override_reason: reason || null,
      });
      const data = res.data.data;
      if (data.published) {
        toast.success(data.override_used ? 'Session published with override' : 'Session published successfully');
        setValidationResult(null);
        setConflictResult(null);
        setShowOverrideModal(false);
        loadSession();
      } else {
        // Publish blocked
        setValidationResult(data);
        setConflictResult(data.conflict_validation);
        if (data.allows_override) {
          setShowOverrideModal(true);
        } else {
          toast.error(data.block_reasons?.[0] || 'Publish blocked');
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.response?.data?.detail?.error?.message || 'Publish failed';
      toast.error(msg);
    } finally {
      setPublishing(false);
    }
  };

  const handleRunValidation = async () => {
    try {
      const [ratioRes, conflictRes, checklistRes] = await Promise.all([
        api.post(`/group-sessions/${sessionId}/validate-ratio`),
        api.post(`/group-sessions/${sessionId}/validate-conflicts`),
        api.get(`/group-sessions/${sessionId}/publish-checklist`),
      ]);
      setValidationResult({ ratio_validation: ratioRes.data.data, conflict_validation: conflictRes.data.data });
      setConflictResult(conflictRes.data.data);
      setChecklist(checklistRes.data.data);
      toast.success('Validation complete');
    } catch {
      toast.error('Validation failed');
    }
  };

  // Day 4: Activity handlers
  const handleSaveActivity = async () => {
    setSavingActivity(true);
    try {
      if (editingActivity) {
        await api.put(`/group-sessions/${sessionId}/activities/${editingActivity.id}`, activityForm);
        toast.success('Activity updated');
      } else {
        await api.post(`/group-sessions/${sessionId}/activities`, activityForm);
        toast.success('Activity created');
      }
      setShowAddActivity(false);
      setEditingActivity(null);
      setActivityForm({ title: '', start_datetime: '', end_datetime: '', notes: '' });
      loadActivities();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to save activity');
    } finally {
      setSavingActivity(false);
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      await api.delete(`/group-sessions/${sessionId}/activities/${activityId}`);
      toast.success('Activity deleted');
      loadActivities();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to delete activity');
    }
  };

  const openEditActivity = (a) => {
    setEditingActivity(a);
    setActivityForm({ title: a.title, start_datetime: a.start_datetime?.slice(0, 16) || '', end_datetime: a.end_datetime?.slice(0, 16) || '', notes: a.notes || '' });
    setShowAddActivity(true);
  };


  // Participant management
  const handleAddParticipant = async () => {
    if (!selectedParticipant) return;
    setAddingLink(true);
    try {
      await api.post(`/group-sessions/${sessionId}/participants`, {
        participant_id: selectedParticipant,
        notes: linkNotes || null,
      });
      toast.success('Participant added');
      setShowAddParticipant(false);
      setSelectedParticipant('');
      setLinkNotes('');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to add participant');
    } finally {
      setAddingLink(false);
    }
  };

  const handleRemoveParticipant = async (participantId) => {
    try {
      await api.delete(`/group-sessions/${sessionId}/participants/${participantId}`);
      toast.success('Participant removed');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to remove participant');
    }
  };

  // Staff management
  const handleAddStaff = async () => {
    if (!selectedStaff) return;
    setAddingLink(true);
    try {
      await api.post(`/group-sessions/${sessionId}/staff`, {
        staff_id: selectedStaff,
        assignment_role: selectedRole,
        notes: linkNotes || null,
      });
      toast.success('Staff assigned');
      setShowAddStaff(false);
      setSelectedStaff('');
      setSelectedRole('support_worker');
      setLinkNotes('');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to add staff');
    } finally {
      setAddingLink(false);
    }
  };

  const handleRemoveStaff = async (staffId) => {
    try {
      await api.delete(`/group-sessions/${sessionId}/staff/${staffId}`);
      toast.success('Staff removed');
      loadSession();
    } catch (err) {
      toast.error(err.response?.data?.detail?.error?.message || 'Failed to remove staff');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  if (!session) return null;

  const linkedParticipantIds = (session.participants || []).map(p => p.participant_id);
  const linkedStaffIds = (session.staff || []).map(s => s.staff_id);

  const filteredParticipants = allParticipants.filter(p => {
    const id = p.id || p._id;
    if (linkedParticipantIds.includes(id)) return false;
    if (!participantSearch) return true;
    const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
    return name.includes(participantSearch.toLowerCase());
  });

  const filteredStaff = allStaff.filter(s => {
    const id = s.id || s._id;
    if (linkedStaffIds.includes(id)) return false;
    if (!staffSearch) return true;
    return (s.full_name || s.name || '').toLowerCase().includes(staffSearch.toLowerCase());
  });

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="group-session-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/group-sessions')} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{session.title}</h1>
            <Badge className={STATUS_STYLES[session.status]}>{STATUS_LABELS[session.status]}</Badge>
          </div>
          <p className="text-sm text-muted-foreground font-mono mt-1">{session.session_code}</p>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && !editing && (
            <Button variant="outline" onClick={startEdit} data-testid="edit-session-btn"><Edit2 className="h-4 w-4 mr-2" /> Edit</Button>
          )}
          {session.status === 'draft' && (
            <>
              <Button variant="outline" size="sm" onClick={handleRunValidation} data-testid="validate-btn">
                <Shield className="h-4 w-4 mr-2" /> Validate
              </Button>
              <Button variant="default" onClick={() => handlePublish()} disabled={publishing} data-testid="publish-btn">
                {publishing ? 'Publishing...' : 'Publish'}
              </Button>
            </>
          )}
          {session.status === 'published' && (
            <Button variant="default" onClick={() => handleStatusChange('in_progress')} data-testid="start-btn"><Play className="h-4 w-4 mr-2" /> Start Session</Button>
          )}
          {session.status === 'in_progress' && (
            <Button variant="default" onClick={() => handleStatusChange('completed')} data-testid="complete-btn"><CheckCircle className="h-4 w-4 mr-2" /> Complete</Button>
          )}
          {isEditable && (
            <Button variant="destructive" size="sm" onClick={() => handleStatusChange('cancelled')} data-testid="cancel-btn"><XCircle className="h-4 w-4 mr-2" /> Cancel</Button>
          )}
        </div>
      </div>

      {/* Pre-publish Checklist Panel (Day 3) */}
      {session.status === 'draft' && checklist && (
        <Card className={`border-l-4 ${
          checklist.outcome === 'ready' ? 'border-l-emerald-500' :
          checklist.outcome === 'ready_with_warnings' ? 'border-l-amber-500' :
          'border-l-red-500'
        }`} data-testid="publish-checklist">
          <CardContent className="py-4 px-5">
            {/* Top outcome badge */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {checklist.outcome === 'ready' && <CheckCircle className="h-6 w-6 text-emerald-600" />}
                {checklist.outcome === 'ready_with_warnings' && <AlertTriangle className="h-6 w-6 text-amber-600" />}
                {(checklist.outcome === 'cannot_publish' || checklist.outcome === 'override_available') && <XCircle className="h-6 w-6 text-red-600" />}
                <div>
                  <p className={`font-semibold text-sm ${
                    checklist.outcome === 'ready' ? 'text-emerald-800' :
                    checklist.outcome === 'ready_with_warnings' ? 'text-amber-800' : 'text-red-800'
                  }`}>{checklist.outcome_label}</p>
                  <p className="text-xs text-muted-foreground">{checklist.outcome_message}</p>
                </div>
              </div>
              {checklistLoading && <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />}
            </div>

            {/* 5 compact sections */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {checklist.sections.map(section => (
                <div key={section.label} className={`rounded-lg p-3 border ${
                  section.status === 'pass' ? 'bg-emerald-50/50 border-emerald-200' :
                  section.status === 'warning' ? 'bg-amber-50/50 border-amber-200' :
                  'bg-red-50/50 border-red-200'
                }`} data-testid={`checklist-section-${section.label.toLowerCase()}`}>
                  <div className="flex items-center gap-1.5 mb-2">
                    {section.status === 'pass' && <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />}
                    {section.status === 'warning' && <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                    {section.status === 'fail' && <XCircle className="h-3.5 w-3.5 text-red-600" />}
                    <span className="text-xs font-semibold uppercase tracking-wider">{section.label}</span>
                  </div>
                  <div className="space-y-1">
                    {section.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-[11px]">
                        <span className="text-muted-foreground truncate mr-1">{item.label}</span>
                        <span className={`font-medium shrink-0 ${
                          item.status === 'pass' ? 'text-emerald-700' :
                          item.status === 'warning' ? 'text-amber-700' :
                          item.status === 'fail' ? 'text-red-700' : 'text-foreground'
                        }`}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Publish blocked actions */}
            {validationResult?.block_reasons?.length > 0 && (
              <div className="mt-4 pt-3 border-t">
                {validationResult.block_reasons.map((r, i) => (
                  <p key={i} className="text-sm text-red-700">{r}</p>
                ))}
                {validationResult.allows_override && (
                  <Button size="sm" variant="outline" className="mt-2 text-orange-700 border-orange-300 hover:bg-orange-50" onClick={() => setShowOverrideModal(true)} data-testid="open-override-btn">
                    <Shield className="h-3.5 w-3.5 mr-1.5" /> Override & Publish
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {session.status === 'draft' && !checklist && checklistLoading && (
        <Card className="border-l-4 border-l-slate-300">
          <CardContent className="py-4 flex items-center justify-center gap-2">
            <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
            <span className="text-sm text-muted-foreground">Loading pre-publish checklist...</span>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList data-testid="session-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="participants" data-testid="tab-participants">
            Participants ({session.participant_count || 0})
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-staff">
            Staff ({session.staff_count || 0}/{session.required_staff_count})
          </TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities">
            Activities
          </TabsTrigger>
          <TabsTrigger value="service-links" data-testid="tab-service-links">
            Service Links
          </TabsTrigger>
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview">
          {editing ? (
            <Card>
              <CardHeader><CardTitle>Edit Session</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} data-testid="edit-title" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Session Type</Label>
                    <Select value={editForm.session_type} onValueChange={v => setEditForm({...editForm, session_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(SESSION_TYPE_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location Type</Label>
                    <Select value={editForm.location_type} onValueChange={v => setEditForm({...editForm, location_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LOCATION_TYPE_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start</Label>
                    <Input type="datetime-local" value={editForm.start_datetime} onChange={e => setEditForm({...editForm, start_datetime: e.target.value})} />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input type="datetime-local" value={editForm.end_datetime} onChange={e => setEditForm({...editForm, end_datetime: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Required Staff</Label>
                    <Input type="number" min="1" max="20" value={editForm.required_staff_count} onChange={e => setEditForm({...editForm, required_staff_count: e.target.value})} />
                  </div>
                  <div>
                    <Label>Ratio Rule</Label>
                    <Select value={editForm.ratio_rule_id || '__none__'} onValueChange={v => setEditForm({...editForm, ratio_rule_id: v === '__none__' ? '' : v})}>
                      <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {ratioRules.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} rows={3} />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving} data-testid="save-edit-btn"><Save className="h-4 w-4 mr-2" /> {saving ? 'Saving...' : 'Save'}</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Session Details</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Type</span><Badge variant="outline">{SESSION_TYPE_LABELS[session.session_type]}</Badge></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Location</span><span className="text-sm flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{LOCATION_TYPE_LABELS[session.location_type]}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Start</span><span className="text-sm">{session.start_datetime ? new Date(session.start_datetime).toLocaleString() : '—'}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">End</span><span className="text-sm">{session.end_datetime ? new Date(session.end_datetime).toLocaleString() : '—'}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Billing Mode</span><span className="text-sm capitalize">{(session.billing_mode || '').replace(/_/g, ' ')}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Funding Mode</span><span className="text-sm capitalize">{session.funding_mode}</span></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase className="h-4 w-4" /> Staffing & Scope</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Staff</span>
                    <span className={`text-sm font-mono ${session.assigned_staff_count < session.required_staff_count ? 'text-orange-600' : 'text-emerald-600'}`}>
                      {session.assigned_staff_count} / {session.required_staff_count} required
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Participants</span><span className="text-sm">{session.participant_count || 0}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Ratio Validation</span><Badge variant="outline">{session.ratio_validation_status}</Badge></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">State</span><span className="text-sm">{session.state_id || 'Global'}</span></div>
                  <Separator />
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Branch</span><span className="text-sm">{session.branch_id || 'All'}</span></div>
                  {session.notes && (<><Separator /><div><span className="text-sm text-muted-foreground">Notes</span><p className="text-sm mt-1">{session.notes}</p></div></>)}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* PARTICIPANTS TAB */}
        <TabsContent value="participants">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Linked Participants</CardTitle>
                {isEditable && (
                  <Button size="sm" onClick={() => { setShowAddParticipant(true); setParticipantSearch(''); setSelectedParticipant(''); setLinkNotes(''); }} data-testid="add-participant-btn">
                    <UserPlus className="h-4 w-4 mr-2" /> Add Participant
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(session.participants || []).length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No participants linked yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participant</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead>Billing</TableHead>
                      <TableHead>Notes</TableHead>
                      {isEditable && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(session.participants || []).map(p => (
                      <TableRow key={p.id} data-testid={`participant-row-${p.participant_id}`}>
                        <TableCell>
                          <span className="font-medium">{p.participant_name || p.participant_id}</span>
                          {p.participant_status && <Badge variant="outline" className="ml-2 text-xs">{p.participant_status}</Badge>}
                        </TableCell>
                        <TableCell><Badge variant="secondary">{p.attendance_status}</Badge></TableCell>
                        <TableCell><Badge variant="outline">{p.billing_status}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{p.notes || '—'}</TableCell>
                        {isEditable && (
                          <TableCell>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleRemoveParticipant(p.participant_id)} data-testid={`remove-participant-${p.participant_id}`}>
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* STAFF TAB */}
        <TabsContent value="staff">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Briefcase className="h-4 w-4" /> Assigned Staff</CardTitle>
                  <CardDescription>
                    {session.assigned_staff_count} of {session.required_staff_count} required staff assigned
                    {session.assigned_staff_count < session.required_staff_count && (
                      <span className="text-orange-600 ml-2">({session.required_staff_count - session.assigned_staff_count} more needed)</span>
                    )}
                  </CardDescription>
                </div>
                {isEditable && (
                  <Button size="sm" onClick={() => { setShowAddStaff(true); setStaffSearch(''); setSelectedStaff(''); setSelectedRole('support_worker'); setLinkNotes(''); }} data-testid="add-staff-btn">
                    <UserPlus className="h-4 w-4 mr-2" /> Add Staff
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(session.staff || []).length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No staff assigned yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Attendance</TableHead>
                      <TableHead>Notes</TableHead>
                      {isEditable && <TableHead>Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(session.staff || []).map(s => (
                      <TableRow key={s.id} data-testid={`staff-row-${s.staff_id}`}>
                        <TableCell>
                          <div>
                            <span className="font-medium">{s.staff_name || s.staff_id}</span>
                            {s.staff_email && <p className="text-xs text-muted-foreground">{s.staff_email}</p>}
                          </div>
                        </TableCell>
                        <TableCell><Badge variant="outline">{ROLE_LABELS[s.assignment_role] || s.assignment_role}</Badge></TableCell>
                        <TableCell><Badge variant="secondary">{s.attendance_status}</Badge></TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{s.notes || '—'}</TableCell>
                        {isEditable && (
                          <TableCell>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleRemoveStaff(s.staff_id)} data-testid={`remove-staff-${s.staff_id}`}>
                              <UserMinus className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* AI Staff Suggestions Panel */}
          {isEditable && (
            <div className="mt-4">
              <StaffSuggestionsPanel
                sessionId={sessionId}
                refreshKey={session.staff_count}
                onSelectStaff={(staffId) => {
                  setSelectedStaff(staffId);
                  setSelectedRole('support_worker');
                  setShowAddStaff(true);
                }}
              />
            </div>
          )}
        </TabsContent>

        {/* ACTIVITIES TAB (Day 4) */}
        <TabsContent value="activities">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Activity / Program Blocks</CardTitle>
                  <CardDescription>Break this session into timed activity blocks</CardDescription>
                </div>
                {isEditable && (
                  <Button size="sm" onClick={() => { setEditingActivity(null); setActivityForm({ title: '', start_datetime: session.start_datetime?.slice(0, 16) || '', end_datetime: session.end_datetime?.slice(0, 16) || '', notes: '' }); setShowAddActivity(true); }} data-testid="add-activity-btn">
                    <Plus className="h-4 w-4 mr-2" /> Add Activity
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {activities.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No activities yet</p>
                  <p className="text-sm mt-1">Add program blocks to structure this session</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activities.map(a => (
                    <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`activity-row-${a.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-1 bg-primary rounded-full" />
                        <div>
                          <p className="font-medium text-sm">{a.title}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {a.start_datetime ? new Date(a.start_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'} — {a.end_datetime ? new Date(a.end_datetime).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'}
                          </p>
                          {a.notes && <p className="text-xs text-muted-foreground mt-0.5">{a.notes}</p>}
                        </div>
                      </div>
                      {isEditable && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditActivity(a)} data-testid={`edit-activity-${a.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteActivity(a.id)} data-testid={`delete-activity-${a.id}`}><XCircle className="h-3.5 w-3.5" /></Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Create/Edit Dialog */}
          <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle>{editingActivity ? 'Edit Activity' : 'Add Activity Block'}</DialogTitle>
                <DialogDescription>Define a timed activity within this session.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Title *</Label>
                  <Input value={activityForm.title} onChange={e => setActivityForm({...activityForm, title: e.target.value})} placeholder="e.g., Morning Art Session" required data-testid="activity-title" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start</Label>
                    <Input type="datetime-local" value={activityForm.start_datetime} onChange={e => setActivityForm({...activityForm, start_datetime: e.target.value})} data-testid="activity-start" />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input type="datetime-local" value={activityForm.end_datetime} onChange={e => setActivityForm({...activityForm, end_datetime: e.target.value})} data-testid="activity-end" />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={activityForm.notes} onChange={e => setActivityForm({...activityForm, notes: e.target.value})} rows={2} placeholder="Optional notes..." />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddActivity(false)}>Cancel</Button>
                <Button onClick={handleSaveActivity} disabled={!activityForm.title || savingActivity} data-testid="save-activity-btn">
                  {savingActivity ? 'Saving...' : (editingActivity ? 'Update' : 'Add Activity')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* SERVICE LINKS TAB (Day 5) */}
        <TabsContent value="service-links">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Service Delivery Links</CardTitle>
                  <CardDescription>Per-participant service delivery tracking with review workflow</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {serviceLinkSummary && serviceLinkSummary.generated && (
                    <div className="flex items-center gap-3 text-xs mr-3">
                      <span className="text-emerald-600 font-medium">{serviceLinkSummary.ready} ready</span>
                      <span className="text-amber-600 font-medium">{serviceLinkSummary.manual_review} review</span>
                      <span className="text-blue-600 font-medium">{serviceLinkSummary.completed} done</span>
                    </div>
                  )}
                  {session.status !== 'draft' && (
                    <Button size="sm" onClick={handleGenerateLinks} disabled={generatingLinks} data-testid="generate-links-btn">
                      <FileText className="h-4 w-4 mr-2" />
                      {generatingLinks ? 'Generating...' : (serviceLinkSummary?.generated ? 'Refresh Links' : 'Generate Links')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {session.status === 'draft' && (
                <div className="py-8 text-center text-muted-foreground">
                  <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>Session must be published before generating service links</p>
                </div>
              )}
              {session.status !== 'draft' && serviceLinks.length === 0 && (
                <div className="py-8 text-center text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p>No service links generated yet</p>
                  <p className="text-sm mt-1">Click "Generate Links" to create per-participant service delivery records</p>
                </div>
              )}
              {serviceLinks.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Participant</TableHead>
                      <TableHead>Review Status</TableHead>
                      <TableHead>Exception Flags</TableHead>
                      <TableHead>Staff Ref</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serviceLinks.map(link => (
                      <TableRow key={link.id} data-testid={`service-link-row-${link.id}`}>
                        <TableCell>
                          <span className="font-medium">{link.participant_name || link.participant_id?.slice(0, 8)}</span>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            link.review_status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                            link.review_status === 'ready' ? 'bg-blue-100 text-blue-700' :
                            link.review_status === 'manual_review' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>{link.review_status?.replace(/_/g, ' ')}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(link.exception_flags || []).map((f, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] h-5">{f.replace(/_/g, ' ')}</Badge>
                            ))}
                            {(!link.exception_flags || link.exception_flags.length === 0) && <span className="text-xs text-muted-foreground">None</span>}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground">
                          {link.staff_attendance_reference_id ? 'Shared' : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">
                          {link.review_notes || '—'}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => {
                            setReviewingLink(link);
                            setReviewForm({ review_status: link.review_status || 'pending', review_notes: link.review_notes || '' });
                          }} data-testid={`review-link-${link.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Review Dialog */}
          <Dialog open={!!reviewingLink} onOpenChange={() => setReviewingLink(null)}>
            <DialogContent className="sm:max-w-[450px]">
              <DialogHeader>
                <DialogTitle>Review Service Link</DialogTitle>
                <DialogDescription>Update review status for {reviewingLink?.participant_name || 'participant'}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {reviewingLink?.exception_flags?.length > 0 && (
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-xs font-semibold text-amber-800 mb-1">Exception Flags</p>
                    <div className="flex flex-wrap gap-1">
                      {reviewingLink.exception_flags.map((f, i) => (
                        <Badge key={i} variant="outline" className="text-[10px]">{f.replace(/_/g, ' ')}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label>Review Status</Label>
                  <Select value={reviewForm.review_status} onValueChange={v => setReviewForm({...reviewForm, review_status: v})}>
                    <SelectTrigger data-testid="review-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                      <SelectItem value="manual_review">Manual Review</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Review Notes</Label>
                  <Textarea value={reviewForm.review_notes} onChange={e => setReviewForm({...reviewForm, review_notes: e.target.value})} rows={3} placeholder="Notes about this review decision..." data-testid="review-notes-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReviewingLink(null)}>Cancel</Button>
                <Button onClick={handleReviewLink} data-testid="save-review-btn">Save Review</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Participant to Session</DialogTitle>
            <DialogDescription>Select a participant to link to this group session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Search Participants</Label>
              <Input value={participantSearch} onChange={e => setParticipantSearch(e.target.value)} placeholder="Search by name..." data-testid="search-participant" />
            </div>
            <div className="max-h-[200px] overflow-y-auto border rounded-md">
              {filteredParticipants.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">No available participants</p>
              ) : filteredParticipants.slice(0, 20).map(p => {
                const id = p.id || p._id;
                const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unnamed';
                return (
                  <button key={id} type="button" className={`w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors ${selectedParticipant === id ? 'bg-accent' : ''}`} onClick={() => setSelectedParticipant(id)} data-testid={`select-participant-${id}`}>
                    {name}
                  </button>
                );
              })}
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={linkNotes} onChange={e => setLinkNotes(e.target.value)} placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipant(false)}>Cancel</Button>
            <Button onClick={handleAddParticipant} disabled={!selectedParticipant || addingLink} data-testid="confirm-add-participant">
              {addingLink ? 'Adding...' : 'Add Participant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaff} onOpenChange={setShowAddStaff}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Staff to Session</DialogTitle>
            <DialogDescription>Select a staff member and their role for this session.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Search Staff</Label>
              <Input value={staffSearch} onChange={e => setStaffSearch(e.target.value)} placeholder="Search by name..." data-testid="search-staff" />
            </div>
            <div className="max-h-[200px] overflow-y-auto border rounded-md">
              {filteredStaff.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">No available staff</p>
              ) : filteredStaff.slice(0, 20).map(s => {
                const id = s.id || s._id;
                const name = s.full_name || s.name || 'Unknown';
                return (
                  <button key={id} type="button" className={`w-full text-left px-4 py-2 text-sm hover:bg-muted transition-colors ${selectedStaff === id ? 'bg-accent' : ''}`} onClick={() => setSelectedStaff(id)} data-testid={`select-staff-${id}`}>
                    {name} <span className="text-xs text-muted-foreground">({s.email || ''})</span>
                  </button>
                );
              })}
            </div>
            <div>
              <Label>Assignment Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="input-assignment-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Input value={linkNotes} onChange={e => setLinkNotes(e.target.value)} placeholder="Optional notes..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStaff(false)}>Cancel</Button>
            <Button onClick={handleAddStaff} disabled={!selectedStaff || addingLink} data-testid="confirm-add-staff">
              {addingLink ? 'Assigning...' : 'Assign Staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day 3: Override Modal */}
      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-orange-600" /> Override & Publish
            </DialogTitle>
            <DialogDescription>
              Validation has flagged issues that normally prevent publishing. As an authorized user, you can override and publish with a mandatory reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {validationResult?.block_reasons?.map((r, i) => (
              <div key={i} className="flex items-start gap-2 text-sm bg-red-50 p-3 rounded-lg">
                <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                <span className="text-red-700">{r}</span>
              </div>
            ))}
            <div>
              <Label>Override Reason (required, min 5 characters)</Label>
              <Textarea
                value={overrideReason}
                onChange={e => setOverrideReason(e.target.value)}
                placeholder="Explain why you are overriding the validation..."
                rows={3}
                data-testid="override-reason-input"
              />
              <p className="text-xs text-muted-foreground mt-1">This reason will be recorded in the audit log.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowOverrideModal(false); setOverrideReason(''); }}>Cancel</Button>
            <Button
              variant="default"
              className="bg-orange-600 hover:bg-orange-700"
              disabled={overrideReason.trim().length < 5 || publishing}
              onClick={() => handlePublish(true, overrideReason)}
              data-testid="confirm-override-btn"
            >
              {publishing ? 'Publishing...' : 'Override & Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
